import type { BellNotificationListResponse, SessionUser } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { listMyNotifications, markAllMyNotificationsRead, markMyNotificationRead } from './api';
import { queryKeys } from './queryKeys';

/**
 * THE BELL'S DATA — the bell table and its endpoints (spec row 14, SIR-7690).
 *
 * The server writes every notification and its final wording: who sees what is its rule
 * (an administrator sees catalog and Other submissions, HR only the catalog ones it may act
 * on, everybody the answers to their own requests), and read state lives in its table, so
 * it follows the person across browsers. This file only reads it, and marks rows read.
 *
 * It replaces the wave 1 stand-in, which derived rows from the Queue and kept read state in
 * localStorage. The bell component above it keeps its shape.
 */

/** BundleUp's cadence: light polling for the badge, paused while the tab is hidden. */
const POLL_MS = 30_000;
/** Newest first, capped like BundleUp's panel page. */
const LIST_LIMIT = 30;

export interface BellNotification {
  /** The server's row id — what Mark read names. */
  id: string;
  /** The server's stable key for the row. */
  key: string;
  title: string;
  body: string;
  /** ISO timestamp the row sorts and ages by. */
  at: string;
  /** Where a click goes. */
  link: string;
  read: boolean;
}

export interface BellFeed {
  readonly notifications: readonly BellNotification[];
  /** The server's count over ALL the person's rows, not only the 30 shown. */
  readonly unreadCount: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly markRead: (row: BellNotification) => void;
  readonly markAllRead: () => void;
}

/**
 * The one bell read. `user` is part of the call so a signed-out moment reads nothing; the
 * endpoint itself takes no user parameter (it is always the caller's own), so neither does
 * the key.
 */
export function useBellQuery(user: SessionUser | null) {
  return useQuery({
    queryKey: queryKeys.myNotifications,
    queryFn: () => listMyNotifications(LIST_LIMIT),
    enabled: user !== null,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useBell(user: SessionUser): BellFeed {
  const queryClient = useQueryClient();
  const bell = useBellQuery(user);

  const notifications = useMemo(
    () =>
      (bell.data?.data ?? []).map((row) => ({
        id: row.id,
        key: row.key,
        title: row.title,
        body: row.body,
        at: row.at,
        link: row.link,
        read: row.readAt !== null,
      })),
    [bell.data],
  );

  /** Shows the read at once; the server's answer (or the next poll) settles it. */
  function patchCache(update: (data: BellNotificationListResponse) => BellNotificationListResponse) {
    queryClient.setQueryData<BellNotificationListResponse>(queryKeys.myNotifications, (data) =>
      data === undefined ? data : update(data),
    );
  }

  const readOne = useMutation({
    mutationFn: (id: string) => markMyNotificationRead(id),
    onMutate: (id) => {
      const at = new Date().toISOString();
      patchCache((data) => {
        const target = data.data.find((row) => row.id === id);
        if (target === undefined || target.readAt !== null) return data;
        return {
          data: data.data.map((row) => (row.id === id ? { ...row, readAt: at } : row)),
          meta: { ...data.meta, unreadCount: Math.max(0, data.meta.unreadCount - 1) },
        };
      });
    },
    onSuccess: (result) => {
      patchCache((data) => ({
        data: data.data.map((row) => (row.id === result.notification.id ? result.notification : row)),
        meta: { ...data.meta, unreadCount: result.meta.unreadCount },
      }));
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myNotifications });
    },
  });

  const readAll = useMutation({
    mutationFn: markAllMyNotificationsRead,
    onMutate: () => {
      const at = new Date().toISOString();
      patchCache((data) => ({
        data: data.data.map((row) => (row.readAt === null ? { ...row, readAt: at } : row)),
        meta: { ...data.meta, unreadCount: 0 },
      }));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myNotifications });
    },
  });

  return {
    notifications,
    unreadCount: bell.data?.meta.unreadCount ?? 0,
    isLoading: bell.isLoading,
    isError: bell.isError,
    markRead: (row) => {
      if (!row.read) readOne.mutate(row.id);
    },
    markAllRead: () => {
      readAll.mutate();
    },
  };
}
